import startCase from "lodash/startCase";
import moment from "moment";
import CanadaLogo from "assets/svg/countries/canada-logo.svg";
import BritishLogo from "assets/svg/countries/british-logo.svg";
import NigerianLogo from "assets/svg/countries/nigerian-logo.svg";
import USLogo from "assets/svg/countries/us-logo.svg";
import { useCallback, useEffect, useState } from "react";

export const handleReturnCountryCurrency = (countrySymbol: CountrySymbols) => {
  switch (countrySymbol) {
    case "USD":
      return "$";
    case "CAD":
      return "CA$";
    case "GBP":
      return "£";
    default:
      return "₦";
  }
};

export const handleReturnCountryCode = (
  phoneCode: string,
  countryOfResidence: string
) => {
  let currency;
  if (phoneCode === "+1" && countryOfResidence === "CA") {
    currency = "CAD";
  } else if (phoneCode === "+1" && countryOfResidence === "US") {
    currency = "USD";
  } else if (phoneCode === "+44") {
    currency = "GBP";
  } else {
    currency = "NGN";
  }
  return currency;
};

export const handleReturnPhoneNumber = (value: string) => {
  let phoneObj: { phone_code: string; phoneNumber: string } = {
    phone_code: "",
    phoneNumber: "",
  };

  switch (true) {
    case value.startsWith("+1"):
      phoneObj.phone_code = value.substring(0, 2);
      phoneObj.phoneNumber = value.slice(2);
      break;
    case value.startsWith("+44"):
      phoneObj.phone_code = value.substring(0, 3);
      phoneObj.phoneNumber = value.slice(3);
      break;

    default:
      phoneObj.phone_code = value.substring(0, 4);
      phoneObj.phoneNumber = value.slice(4);
  }

  return phoneObj;
};

export const formatCurrency = (
  num: number | string,
  space = true,
  shouldReturnCurrencyWithAmount = true,
  currencySymbol?: CountrySymbols | string
) => {
  const localNumber = typeof num === "string" ? Number(num) : num;

  const currency = shouldReturnCurrencyWithAmount
    ? handleReturnCountryCurrency(currencySymbol as CountrySymbols)
    : "";

  return Math.sign(localNumber || 0) === -1
    ? `-${currency} ${space ? " " : ""}${localNumber
        .toFixed(2)
        .replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1,")
        .slice(1)}`
    : `${currency}${space ? " " : ""}${localNumber
        .toFixed(2)
        .replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1,")}`;
};

export const getUser = () => {
  let parsedUser, user;
  user = localStorage.getItem("user");
  if (user) {
    parsedUser = JSON.parse(user) as IUser;
  }

  return parsedUser;
};

export const getNestedValue = (obj: any, path: string) => {
  return path.split(".").reduce((acc, part) => acc && acc[part], obj);
};

export const renderTimeField = (text: string | number) => {
  return moment(text).format("MMM Do YY");
};

export const truncateMiddleText = (text: string = '', maxLength: number = 15) => {
  if (text.length <= maxLength) {
    return text;
  }

  var mid = Math.ceil(text.length / 2);
  var before = text.substring(0, mid - Math.ceil((maxLength - 3) / 2));
  var after = text.substring(mid + Math.floor((maxLength - 3) / 2));

  return before + "..." + after;
};

export const handleGetCountryLogo = (country: CountrySymbols) => {
  switch (country?.trim()) {
    case "USD":
      return USLogo;
    case "CAD":
      return CanadaLogo;
    case "GBP":
      return BritishLogo;
    case "EUR":
      return BritishLogo;
    default:
      return NigerianLogo;
  }
};

export const returnPhoneCode = (country: CountrySymbols | any) => {
  switch (country) {
    case "USD":
      return "+1";
    case "CAD":
      return "+1";
    case "GBP":
      return "+44";  
    case "NGN":
      return "+234";
    case "AUT":
      return "+43";
    case "BEL":
      return "+32";
    case "BGR":
      return "+359";
    case "HRV":
      return "+385";
    case "CYP":
      return "+357";
    case "CZE":
      return "+420";
    case "DNK":
      return "+45";
    case "EST":
      return "+372";
    case "FIN":
      return "+358";
    case "FRA":
      return "+33";
    case "DEU":
      return "+49";
    case "GRC":
      return "+30";
    case "HUN":
      return "+36";
    case "IRL":
      return "+353";
    case "ITA":
      return "+39";
    case "LVA":
      return "+371";
    case "LTU":
      return "+370";
    case "LUX":
      return "+352";
    case "MLT":
      return "+356";
    case "NLD":
      return "+31";
    case "POL":
      return "+48";
    case "ROU":
      return "+40";
    case "PRT":
      return "+351";
    case "SVK":
      return "+421";
    case "SVN":
      return "+386";
    case "ESP":
      return "+34";
    case "SWE":
      return "+46";
    default:
      return "+234";
  }
};

export const isSuperUser = (user: AdminRole) => {
  switch (user) {
    case "advanced":
    case "super_admin":
      return true;
    default:
      return false;
  }
};

export const isJustSuperAdmin = (role: any) => {
  switch (role) {
    case "super_admin":
      return true;
    default:
      return false;
  }
}

export const useCheckPermission = (accountDetails: any, permitSlugs: string[]) => {
  const [permissionSlugs, setPermissionSlugs] = useState<string[]>([]);

  useEffect(() => {
    const slugs = accountDetails?.admin?.permissions?.map((permission: any) => permission?.slug) ?? [];
    setPermissionSlugs(slugs);
  }, [accountDetails]);

  return permitSlugs?.some(permitSlug => permissionSlugs?.includes(permitSlug));
};

export const transformText = (value: string) => startCase(value);

export function allCaps(text: string | number) {
  return String(text ?? '').toUpperCase()
}

export function allLower(text: string | number) {
  return String(text ?? '')?.toLowerCase()
}

export const truncateText = (text: string = '', maxLength: number): string => {
  if (text.length <= maxLength) {
    return text;
  }
  return text.slice(0, maxLength) + "...";
};

export const useParsedNumber = () => {
  const parseNumber = useCallback((value: string | number) => {
    
    if (typeof value === "string") {
      return Number(value.replace(/,/g, ""));
    }
    return Number(value);
  }, []);

  return { parseNumber };
};